/**
 * Portal Tenant + Brand Resolver
 *
 * Parses the current browser hostname into a `{ tenantAlias, brandAlias }`
 * pair based on the portal subdomain convention:
 *
 *   Bare:   {tenant}.my.workd.com
 *   Branded: {brand}--{tenant}.my.workd.com
 *
 * The URL shape uses a single subdomain label — the public CDN's wildcard ACM
 * cert only covers one subdomain level, so nested forms like
 * `{brand}.{tenant}.my…` wouldn't be TLS-valid. Tenant-alias underscores are
 * encoded as dashes in the host (underscores are invalid in DNS hostnames), so
 * the brand/tenant delimiter is a DOUBLE dash: a single dash is always an
 * encoded tenant underscore, so the split on the first `--` is unambiguous and
 * the tenant segment is decoded `-`→`_`. Must match libs/core buildPortalUrl.
 *
 * Custom domains (customer-owned hostnames like `shop.customer.com`) are
 * not parsed here — `brandAlias` comes back null and the backend bootstrap
 * endpoint (`GET /api/portal/site`) is the source of truth for those.
 *
 * Native (Capacitor) builds bypass hostname parsing entirely. The tenant
 * (and optional brand) is baked into the bundle at build time via
 * `VITE_TENANT_ALIAS` / `VITE_BRAND_ALIAS` so the native shell points at
 * exactly one customer's portal regardless of what URL the embedded
 * webview reports for itself.
 */

// Ordered most-specific first so "local.my" doesn't partial-match the
// production pattern. Legacy "portal" suffixes are kept for backward
// compatibility with older portal URLs during transition.
const PORTAL_SUFFIXES = [
	'.local.my.workd.com',
	'.staging.my.workd.com',
	'.my.workd.com',
	'.local.portal.workd.com',
	'.staging.portal.workd.com',
	'.portal.workd.com',
];

export interface PortalHostnameParts {
	tenantAlias: string;
	brandAlias: string | null;
}

interface CapacitorRuntime {
	isNativePlatform?: () => boolean;
}

/**
 * True when the bundle is running inside a Capacitor native shell
 * (iOS / Android). Detected via the `Capacitor` global that the
 * `@capacitor/core` runtime injects on window — kept as a runtime
 * check (rather than an `import` from `@capacitor/core`) so the web
 * bundle has no dependency on the native runtime.
 */
function isNativePlatform(): boolean {
	if (typeof window === 'undefined') {
		return false;
	}
	const cap = (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor;
	return cap?.isNativePlatform?.() === true;
}

/**
 * Native-build override. Returns the parts baked at build time when running
 * inside a Capacitor shell, or null when not native (so the hostname parser
 * runs as normal in the browser).
 */
function nativePartsFromEnv(): PortalHostnameParts | null {
	if (!isNativePlatform()) {
		return null;
	}
	const tenantAlias = import.meta.env.VITE_TENANT_ALIAS;
	if (tenantAlias == null || tenantAlias.length === 0) {
		return null;
	}
	const brandAlias = import.meta.env.VITE_BRAND_ALIAS;
	return {
		tenantAlias,
		brandAlias: brandAlias != null && brandAlias.length > 0 ? brandAlias : null,
	};
}

/**
 * Parse a hostname into `{ tenantAlias, brandAlias }`.
 *
 * Returns null when the hostname is a bare portal domain with no subdomain,
 * or when the hostname doesn't match a known portal suffix (e.g. a custom
 * domain — callers fall back to the backend bootstrap endpoint for those).
 */
export function parsePortalHostname(
	hostname: string,
): PortalHostnameParts | null {
	const lower = hostname.toLowerCase();

	for (const suffix of PORTAL_SUFFIXES) {
		if (!lower.endsWith(suffix)) {
			continue;
		}

		const label = lower.slice(0, -suffix.length);

		if (label.length === 0) {
			return null;
		}

		// Single-label-only — portal ACM cert doesn't cover nested subdomains.
		if (label.includes('.')) {
			return null;
		}

		const sepIndex = label.indexOf('--');

		if (sepIndex === -1) {
			// Bare tenant URL — decode encoded underscores.
			return { tenantAlias: label.replace(/-/g, '_'), brandAlias: null };
		}

		const brandAlias = label.slice(0, sepIndex);
		const tenantAlias = label.slice(sepIndex + 2).replace(/-/g, '_');

		if (brandAlias.length === 0 || tenantAlias.length === 0) {
			return null;
		}

		return { tenantAlias, brandAlias };
	}

	// Fallback for localhost-ish environments in tests where the portal
	// suffix isn't set up — take the first subdomain if there are enough.
	const parts = lower.split('.');
	if (parts.length >= 4) {
		const label = parts[0] ?? '';
		const sepIndex = label.indexOf('--');
		if (sepIndex === -1) {
			return { tenantAlias: label.replace(/-/g, '_'), brandAlias: null };
		}
		return {
			brandAlias: label.slice(0, sepIndex),
			tenantAlias: label.slice(sepIndex + 2).replace(/-/g, '_'),
		};
	}

	return null;
}

/**
 * Extract the tenant alias from the current hostname.
 *
 * Kept for backward compatibility with existing call sites. New code should
 * prefer `parseCurrentPortalHostname()` when it also needs the brand alias.
 */
export function getTenantAlias(): string | null {
	const parsed = parseCurrentPortalHostname();
	return parsed?.tenantAlias ?? null;
}

/**
 * Extract the brand (portal_site alias) from the current hostname, or null
 * when the request targets the tenant's default brand (no prefix) or a
 * custom domain.
 */
export function getBrandAlias(): string | null {
	const parsed = parseCurrentPortalHostname();
	return parsed?.brandAlias ?? null;
}

/**
 * Full parse of `window.location.hostname` — returns both parts. In native
 * Capacitor builds this short-circuits to the bundle-baked tenant/brand
 * since the embedded webview's hostname (capacitor://localhost) carries
 * no useful tenant information.
 */
export function parseCurrentPortalHostname(): PortalHostnameParts | null {
	const native = nativePartsFromEnv();
	if (native != null) {
		return native;
	}
	return parsePortalHostname(window.location.hostname);
}

/**
 * Build the value that should be sent in the `X-Portal-Tenant` header so
 * the backend middleware can resolve both tenant and brand from a single
 * header (required in dev because the Vite proxy rewrites Origin, and
 * required in native builds because the webview's hostname is opaque).
 *
 * Format: `<brand>--<tenant>` when a brand prefix is present, otherwise
 * just `<tenant>`. Double-dash delimiter mirrors the host; the backend's
 * `X-Portal-Tenant` parser splits on `--` and decodes `-`→`_` (a raw
 * underscore tenant alias here is a no-op through that decode).
 */
export function getPortalTenantHeader(): string | null {
	const parsed = parseCurrentPortalHostname();

	if (parsed == null) {
		return null;
	}

	if (parsed.brandAlias != null) {
		return `${parsed.brandAlias}--${parsed.tenantAlias}`;
	}

	return parsed.tenantAlias;
}
